import React, { useEffect, useMemo, useState } from "react";
import { Field } from "react-final-form";
import styled from "styled-components";
import { Typography } from "../Typography";
import { InfoTooltip } from "../Tooltip/InfoTooltip";
import OpenIcon from '../../assets/selectOpen.svg';
import CloseIcon from '../../assets/select.svg';
import OpenDowenIcon from '../../assets/openDowen.svg'
import CloseUpIcon from '../../assets/closeDowen.svg';

interface Country {
  key: string;
  label: string;
}

interface Option {
  key: string;
  label: string;
  selectable: boolean;
  children: Country[];
}

type InitialItem = {
  name: string;
  id?: string;
};

interface FormSelectProps {
  name: string;
  items: Option[];
  placeholder?: string;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  initialValue?: string[]; 
  display: "chip" | "comma" | "count";
  selectionMode: string;
  filter?: boolean;
  panelHeaderTemplate?: () => React.ReactNode;
  setIdData: (idData: string[]) => void;
  setOpenedContinents?: (openedContinents: string[]) => void;
  openedContinents?: string[];
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const LabelContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;
const SearchInput = styled.input`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-bottom: 8px;
  width: 100%;
  cursor: pointer;
  outline: none;
`;
const DropdownItem = styled.div<{ selected?: boolean }>`
  padding: 16px 8px 16px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const DropdownCountryItem = styled.div<{ selected?: boolean }>`
  padding: 8px 8px 8px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const DropdownList = styled.div`
  display: flex;
  flex-direction: column;
`;
const CountriesContainer = styled(DropdownList)`
  margin-top: 8px;
  max-height: 365px;
  overflow-y: auto;
`;
const InputBox = styled.div`
  width: 253px;
  height: 33px;
  border-radius: 8px;
  border: 1px solid #000000;
  outline: none;
  padding: 6px 15px 7px 15px;
  cursor: pointer;
  font-family: 'SF Pro Display', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #000000;
`;
const DropdownListBox = styled.div`
  position: absolute;
  background: #fff;
  border-radius: 10px;
  margin-top: 4px;
  z-index: 1000;
  width: 374px;
  padding: 19px 16px 0 16px;
  bottom: 39px;
  box-shadow: 0px 0px 10px 3px #BCBCBFAD;
`;
const IconBox = styled.div`
  position: absolute;
  right: 8px;
  top: 6px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;
const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #6777FF;
  appearance: none;
  cursor: pointer;
  position: relative;
  background-color: ${(props) => (props.checked ? "#6777FF" : "#fff")};

  &:checked::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 6px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;
const ClearAll = styled.div`
  height: 44px;
  display: flex;
  flex-direction: column;
  padding-top: 10px;
  color: #00000080;
  cursor: pointer;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  text-decoration: underline;
`;
const DropdownItemBox = styled.div``;

export const FormTreeSelect: React.FC<FormSelectProps> = ({
  name,
  items,
  label,
  tooltip,
  initialValue,
  setIdData,
  setOpenedContinents,
  openedContinents,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [openChild, setOpenChild] = useState('');

  const opened: string[] = openedContinents ?? [];

  const safeSetOpened = (updater: ((prev: string[]) => string[]) | string[]) => {
    if (!setOpenedContinents) return; 
    if (typeof updater === "function") {
      const next = (updater as (prev: string[]) => string[])(opened);
      setOpenedContinents(next);
    } else {
      setOpenedContinents(updater);
    }
  };
  useEffect(() => {
    setIdData(opened);
  }, [opened, setIdData]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return items
      .map((continent) => ({
        ...continent,
        children: continent.children.filter((c) =>
          c.label.toLowerCase().includes(term)
        ),
      }))
      .filter(
        (c) =>
          c.label.toLowerCase().includes(term) || c.children.length > 0
      );
  }, [items, searchTerm]);

  const toggleContinent = (key: string, parentKey?: string) => {
    safeSetOpened((prev) => {
      const newState = [...prev];

      if (!parentKey) {
        const continent = filteredItems.find((c) => c.key === key);
        if (!continent) return prev;

        const childrenKeys = continent.children.map((c) => String(c.key));

        if (newState.includes(key)) {
          return newState.filter((k) => k !== key && !childrenKeys.includes(k));
        } else {
          return [...newState, key, ...childrenKeys].filter(
            (value, index, self) => self.indexOf(value) === index
          );
        }
      } else {
        if (newState.includes(key)) {
          const filtered = newState.filter((k) => k !== key);
          const parent = filteredItems.find((c) => c.key === parentKey);
          if (parent && parent.children.every((c) => !filtered.includes(String(c.key)))) {
            return filtered.filter((k) => k !== parentKey);
          }
          return filtered;
        } else {
          const added = [...newState, key];
          const parent = filteredItems.find((c) => c.key === parentKey);
          if (parent && parent.children.every((c) => added.includes(String(c.key)))) {
            added.push(parentKey);
          }
          return added;
        }
      }
    });
  };

  const toggleSelectAll = () => {
    safeSetOpened((prev) => {
      const allKeys = filteredItems.flatMap((continent) => [
        continent.key,
        ...(continent.children.map((c) => String(c.key)) || []),
      ]);
      if (allKeys.every((key) => prev.includes(key))) {
        return prev.filter((k) => !allKeys.includes(k));
      } else {
        return [...prev, ...allKeys.filter((k) => !prev.includes(k))];
      }
    });
  };

  const isContinentChecked = (continent: any) => {
    const allChildrenSelected = continent.children.every((country: any) =>
      !!opened.find((value: any) => value === country.key)
    );
    return opened.includes(continent.key) || allChildrenSelected;
  };

  useEffect(() => {
    if (initialValue && Array.isArray(initialValue)) {
      const initialKeys = (initialValue as unknown as InitialItem[])
        .map(val => {
          const matchingChild = filteredItems
            .flatMap(continent => continent.children ?? [])
            .find(child => child.label === (val as any).name);
          return matchingChild?.key;
        })
        .filter(Boolean) as string[];

      safeSetOpened(initialKeys);
    }
  }, []);

  return (
    <Field name={name}>
      {({ input }) => (
        <Container>
          {label && (
            <LabelContainer>
              <Typography variant="body6">
                <label htmlFor={name}>{label}</label>
              </Typography>
              {tooltip && <InfoTooltip name={`tooltip-${name}`} tooltip={tooltip} />}
            </LabelContainer>
          )}
          <div onClick={() => setOpen(!open)} style={{ position: "relative" }}>
            <InputBox>Country</InputBox>
            <IconBox>{open ? <OpenIcon /> : <CloseIcon />}</IconBox>
            {open && (
              <DropdownListBox>
                <SearchInput
                  type="text"
                  placeholder={"Search country"}
                  value={searchTerm}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTerm(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <DropdownList onClick={(e) => e.stopPropagation()}>
                  <DropdownItem
                    selected={filteredItems.every((c) => opened.includes(c.key))}
                    style={{ borderBottom: "1px solid #00000033" }}
                  >
                    <Checkbox
                      type="checkbox"
                      id="select-all"
                      // checked={filteredItems.every((c) => opened.includes(c.key)) || opened.length >= 250}
                      checked={true}
                      onChange={toggleSelectAll}
                    />
                    <label
                      htmlFor="select-all"
                      style={{ cursor: "pointer" }}
                    >
                      Select All
                    </label>
                  </DropdownItem>

                  <DropdownItemBox>
                    {filteredItems.map((continent) => (
                      <div key={continent.key}>
                        <DropdownItem
                          selected={opened.includes(continent.key)}
                          style={
                            openChild !== continent.key
                              ? { borderBottom: "1px solid #00000033" }
                              : { borderTop: "1px solid #00000033" }
                          }
                          onClick={() => {
                            input.onChange(continent.key)
                          }}
                        >
                          <label
                            htmlFor={continent.label}
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                              <Checkbox
                                type="checkbox"
                                id={continent.label}
                                // checked={isContinentChecked(continent)}
                                checked={true}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleContinent(continent.key);
                                }}
                              />
                              {continent.label}
                            </div>
                          </label>
                          <div
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setOpenChild(openChild === continent.key ? "" : continent.key);
                            }}
                            style={{
                              width: "30px",
                              height: "30px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                          >
                            {openChild === continent.key ? <OpenDowenIcon /> : <CloseUpIcon />}
                          </div>
                        </DropdownItem>
                        {openChild === continent.key && (
                          <CountriesContainer style={{ marginLeft: "16px" }}>
                            {continent.children.map((country) => (
                              <DropdownCountryItem
                                key={country.key}
                                selected={input.value === country.key}
                                onClick={() => {
                                  input.onChange(country.key)
                                }}
                              >
                                <Checkbox
                                  type="checkbox"
                                  id={country.label}
                                  // checked={opened.includes(country.key)}
                                  checked={true}
                                  onChange={() => toggleContinent(country.key, continent.key)}
                                />
                                <label htmlFor={country.label}>{country.label}</label>
                              </DropdownCountryItem>
                            ))}
                          </CountriesContainer>
                        )}
                      </div>
                    ))}
                  </DropdownItemBox>
                  <ClearAll onClick={() => safeSetOpened([])}>Clear All</ClearAll>
                </DropdownList>
              </DropdownListBox>
            )}
          </div>
        </Container>
      )}
    </Field>
  );
};
